import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import ProductTemplate from '../../../components/feature/ProductTemplate';
import { useSEO } from '../../../utils/seo';
import { useState } from 'react';

const WindowsDoorsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    quantity: '',
    timeline: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    setErrorMessage('');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6f4ftbv53dtlm7j9gtg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          quantity: '',
          timeline: '',
          message: '',
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to submit. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  useSEO({
    title: 'Wholesale Windows & Doors | Direct Import Bulk Building Materials | Save 30-40% Factory Direct',
    description: 'Direct import wholesale windows and doors for multi-unit projects. Bulk construction materials supplier for custom home builders, developers, and contractors. Energy Star rated vinyl and aluminum windows, entry doors, patio doors. Save 30-40% on building materials with factory-direct pricing. Wholesale construction supply for commercial and residential projects.',
    keywords: 'wholesale windows doors, direct import building materials, bulk construction materials supplier, vinyl windows wholesale, aluminum windows bulk, save on building materials, factory direct construction supply',
    canonical: `${siteUrl}/products/windows-doors`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Energy-Efficient Windows & Doors',
      description: 'Wholesale windows and doors supplier offering direct import building materials. High-performance window systems and door solutions with 30-40% savings for bulk construction projects.',
      category: 'Windows & Doors',
      brand: {
        '@type': 'Brand',
        name: 'Go Global Inc.',
      },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'CAD',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'Go Global Inc.',
        },
      },
    },
  });

  const productData = {
    category: 'Windows & Doors',
    title: 'Energy-Efficient Windows & Doors',
    subtitle: 'Save 30-40% on Bulk Window & Door Packages',
    description: 'Direct import high-performance windows and doors for multi-family developments, custom homes, and commercial projects. Energy Star rated systems with factory-direct pricing.',
    heroImage: 'https://public.readdy.ai/ai/img_res/af6225e065bcf884c850abf402d7711c.jpg',
    
    whatIncluded: [
      'Complete window and door units',
      'All hardware and locking systems',
      'Weatherstripping and seals',
      'Installation instructions',
      'Energy performance ratings',
      'Warranty documentation',
    ],

    idealFor: [
      'Multi-family developers (condos, apartments)',
      'Custom home builders',
      'Renovation contractors',
      'Commercial building contractors',
      'Property management companies',
    ],

    benefits: [
      {
        icon: 'ri-price-tag-3-line',
        title: 'Save 30-40% on Bulk Orders',
        description: 'Direct factory pricing eliminates distributor markup on multi-unit projects',
      },
      {
        icon: 'ri-leaf-line',
        title: 'Energy Star Certified',
        description: 'High-performance glazing reduces heating and cooling costs by up to 25%',
      },
      {
        icon: 'ri-shield-check-line',
        title: 'Quality Assurance',
        description: 'All products tested to meet Canadian building code requirements',
      },
      {
        icon: 'ri-truck-line',
        title: 'Protected Delivery',
        description: 'Specialized packaging ensures damage-free arrival to your jobsite',
      },
    ],

    products: [
      {
        name: 'Vinyl Windows',
        description: 'Energy-efficient vinyl window systems in casement, slider, and double-hung configurations',
        specs: 'Multi-chamber frames | Low-E glass | Argon fill',
        image: 'https://readdy.ai/api/search-image?query=modern%20white%20vinyl%20windows%20with%20double%20pane%20glass%20and%20energy%20efficient%20design%20installed%20in%20contemporary%20home%20exterior%20with%20clean%20professional%20finish&width=600&height=500&seq=windows-vinyl-001&orientation=portrait',
      },
      {
        name: 'Aluminum Windows',
        description: 'Commercial-grade aluminum framing for large openings and curtain wall systems',
        specs: 'Thermal break | Commercial grade | Custom sizing',
        image: 'https://readdy.ai/api/search-image?query=sleek%20aluminum%20frame%20windows%20in%20commercial%20building%20with%20narrow%20sightlines%20and%20modern%20design%20showing%20professional%20installation%20in%20bright%20setting&width=600&height=500&seq=windows-aluminum-001&orientation=portrait',
      },
      {
        name: 'Entry Door Systems',
        description: 'Fiberglass and steel entry doors with sidelights, transoms, and decorative glass',
        specs: 'Insulated cores | Multi-point locks | Custom finishes',
        image: 'https://readdy.ai/api/search-image?query=elegant%20modern%20entry%20door%20in%20dark%20finish%20with%20glass%20sidelights%20and%20contemporary%20hardware%20installed%20in%20residential%20entrance%20with%20professional%20finish&width=600&height=500&seq=doors-entry-001&orientation=portrait',
      },
      {
        name: 'Patio Doors',
        description: 'Sliding and French patio door systems with multi-point locking and low-E glass',
        specs: 'Multi-point locks | Low-E glass | Smooth operation',
        image: 'https://readdy.ai/api/search-image?query=modern%20sliding%20patio%20doors%20with%20large%20glass%20panels%20and%20slim%20aluminum%20frames%20connecting%20interior%20to%20outdoor%20space%20in%20contemporary%20home&width=600&height=500&seq=doors-patio-001&orientation=portrait',
      },
      {
        name: 'Commercial Doors',
        description: 'Fire-rated, ADA-compliant doors for commercial and institutional applications',
        specs: 'Fire ratings up to 3 hours | ADA compliant | Panic hardware',
        image: 'https://readdy.ai/api/search-image?query=commercial%20building%20entrance%20doors%20with%20aluminum%20frames%20and%20panic%20hardware%20showing%20professional%20installation%20in%20modern%20office%20building%20lobby&width=600&height=500&seq=doors-commercial-001&orientation=portrait',
      },
      {
        name: 'Hardware & Accessories',
        description: 'Premium locks, handles, hinges, and weatherstripping systems in multiple finishes',
        specs: 'Multiple finishes | Grade 1 locks | Weatherstripping',
        image: 'https://readdy.ai/api/search-image?query=premium%20door%20hardware%20including%20modern%20handles%20locks%20and%20hinges%20in%20brushed%20nickel%20finish%20displayed%20professionally%20on%20white%20surface&width=600&height=500&seq=doors-hardware-001&orientation=portrait',
      },
    ],

    specifications: [
      'Energy Star certified options available',
      'Impact-resistant glazing for coastal regions',
      'Custom sizing for non-standard openings',
      'Multiple glazing options: double, triple, Low-E',
      'Fire-rated doors up to 3-hour rating',
      'ADA-compliant hardware and thresholds',
      'Sound-dampening glass options',
      'Hurricane-rated systems available',
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <ProductTemplate data={productData} />
      
      {/* Quote Request Form Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-display font-semibold text-3xl md:text-4xl text-gray-900 mb-4">
              Get Your Windows & Doors Quote
            </h2>
            <p className="text-lg text-gray-600">
              Tell us about your project and receive a detailed cost comparison within 24 hours
            </p>
          </div>

          <form 
            id="windows-doors-quote"
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-10 border border-gray-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="multi-family">Multi-Family Development</option>
                  <option value="custom-home">Custom Home</option>
                  <option value="renovation">Renovation Project</option>
                  <option value="commercial">Commercial Building</option>
                  <option value="institutional">Institutional</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-semibold text-gray-900 mb-2">
                  Approximate Quantity *
                </label>
                <input
                  type="text"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                  placeholder="e.g., 50 windows, 20 doors"
                />
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Timeline *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent cursor-pointer"
                >
                  <option value="">Select timeline</option>
                  <option value="immediate">Immediate (0-3 months)</option>
                  <option value="short">Short-term (3-6 months)</option>
                  <option value="medium">Medium-term (6-12 months)</option>
                  <option value="long">Long-term (12+ months)</option>
                  <option value="planning">Planning phase</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                Project Details
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-600 focus:border-transparent resize-none"
                placeholder="Describe your window and door requirements, styles needed, or any specific questions..."
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {formData.message.length}/500 characters
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  ✓ Thank you! We'll send you a detailed quote within 24 hours.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-medium">
                  {errorMessage}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitStatus === 'submitting'}
              className="w-full px-8 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-base font-semibold rounded-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {submitStatus === 'submitting' ? 'Submitting...' : 'Get My Windows & Doors Quote'}
              {submitStatus !== 'submitting' && <i className="ri-arrow-right-line ml-2"></i>}
            </button>

            <p className="text-xs text-gray-500 text-center mt-4">
              We'll respond within 24 hours with a detailed cost comparison
            </p>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WindowsDoorsPage;